import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import PasswordField from "@/components/PasswordField";
import PasswordStrength from "@/components/PasswordStrength";
import { toast } from "sonner";
import { AtSign, KeyRound, ArrowLeft, MailCheck, ShieldCheck } from "@/lib/icons";
import { useSeo } from "@/lib/seo";
import EmailDeliveryNotice, { withEmailDeliveryNote } from "@/components/EmailDeliveryNotice";

/**
 * 3-step forgot-password flow using SMTP email OTP.
 *   step 1: identifier (username or email)  → /auth/forgot-password/request
 *   step 2: OTP + resend (60s cooldown)     → /auth/forgot-password/verify
 *   step 3: new password (twice)            → /auth/forgot-password/reset
 */
export default function ForgotPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [cooldown, setCooldown] = useState(0);
  useSeo({ title: "Reset Password | PRONET", noindex: true });

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const requestOtp = async (e) => {
    if (e) e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password/request", { identifier: identifier.trim() });
      setOtpToken(data.otp_token);
      toast.success(withEmailDeliveryNote(data.message || "If an account exists, a code has been sent."));
      setStep(2);
      setCooldown(60);
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password/verify", { otp_token: otpToken, otp });
      setResetToken(data.reset_token);
      setStep(3);
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setErr("");
    if (newPw.length < 8) { setErr("Password must be at least 8 characters"); return; }
    if (newPw !== confirmPw) { setErr("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/forgot-password/reset", { reset_token: resetToken, new_password: newPw });
      toast.success("Password reset. Please sign in with your new password.");
      nav("/login", { replace: true });
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="relative container-tight py-12 md:py-20 max-w-md">
      <div className="blobs-layer">
        <div className="blob blob-violet w-[400px] h-[400px] -top-20 -left-20 opacity-45" />
        <div className="blob blob-magenta w-[380px] h-[380px] bottom-0 right-0 opacity-35" style={{ animationDelay: "-9s" }} />
      </div>

      <div className="relative glass rounded-3xl p-6 sm:p-10 fade-up-slow">
        <Link to="/login" className="inline-flex items-center gap-1 text-xs text-white/50 hover:text-fuchsia-300">
          <ArrowLeft size={12} /> Back to sign in
        </Link>
        <div className="mt-3 overline text-fuchsia-300">Forgot password</div>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl leading-none text-white">
          {step === 1 && <>Reset your <span className="italic text-gradient">password</span></>}
          {step === 2 && <>Enter the <span className="italic text-gradient">code</span></>}
          {step === 3 && <>Choose a <span className="italic text-gradient">new password</span></>}
        </h1>

        {step === 1 && (
          <form onSubmit={requestOtp} className="mt-8 space-y-4">
            <p className="text-sm text-white/60">Enter your username or registered email. We'll send a 6-digit code to your linked email.</p>
            <div>
              <label className="overline text-white/60 text-[10px]">Username or email</label>
              <div className="relative mt-2">
                <AtSign size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <Input required data-testid="fp-identifier"
                  className="pl-9 h-11 bg-white/5 border-white/10 text-white"
                  placeholder="Type here"
                  value={identifier} onChange={(e) => setIdentifier(e.target.value)} />
              </div>
            </div>
            {err && <ErrBox msg={err} />}
            <button type="submit" disabled={busy} data-testid="fp-request-submit"
              className="btn-glow w-full h-12 rounded-full text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
              <MailCheck size={15} /> {busy ? "Sending…" : "Send code"}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={verifyOtp} className="mt-8 space-y-4">
            <p className="text-sm text-white/60">
              We sent a 6-digit code to your registered email. It expires in 5 minutes.
            </p>
            <EmailDeliveryNotice />
            <div>
              <label className="overline text-white/60 text-[10px]">Verification code</label>
              <Input required data-testid="fp-otp" maxLength={6} inputMode="numeric" autoComplete="one-time-code"
                className="mt-2 h-12 text-center text-2xl tracking-[0.4em] bg-white/5 border-white/10 text-white"
                value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} />
            </div>
            {err && <ErrBox msg={err} />}
            <button type="submit" disabled={busy || otp.length !== 6} data-testid="fp-verify-submit"
              className="btn-glow w-full h-12 rounded-full text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
              <ShieldCheck size={15} /> {busy ? "Verifying…" : "Verify code"}
            </button>
            <button type="button"
              disabled={cooldown > 0 || busy}
              onClick={() => requestOtp()}
              data-testid="fp-resend"
              className="w-full text-xs text-white/50 hover:text-fuchsia-300 disabled:opacity-40">
              {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
            </button>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={submitReset} className="mt-8 space-y-4">
            <p className="text-sm text-white/60">Choose a new password. At least 8 characters.</p>
            <PwInput label="New password" value={newPw} onChange={setNewPw} testid="fp-new" showStrength />
            <PwInput label="Confirm new password" value={confirmPw} onChange={setConfirmPw} testid="fp-confirm" />
            {err && <ErrBox msg={err} />}
            <button type="submit" disabled={busy} data-testid="fp-reset-submit"
              className="btn-glow w-full h-12 rounded-full text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
              <KeyRound size={15} /> {busy ? "Saving…" : "Reset password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function PwInput({ label, value, onChange, testid, showStrength }) {
  return (
    <div>
      <label className="overline text-white/60 text-[10px]">{label}</label>
      <div className="mt-2">
        <PasswordField value={value} onChange={onChange} testid={testid} autoComplete="new-password" />
      </div>
      {showStrength && <PasswordStrength password={value} testid={`${testid}-strength`} />}
    </div>
  );
}

function ErrBox({ msg }) {
  return (
    <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="fp-error">
      {msg}
    </div>
  );
}
